import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { 
  Users, MessageSquare, FileText, DollarSign,
  CheckCircle, XCircle, Clock, Eye,
  Loader2, Search, Filter, ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color = 'copper' }) {
  const colors = {
    copper: 'bg-copper/10 text-copper',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-navy">{value}</p>
          <p className="text-sm text-slate-600">{label}</p>
        </div>
      </div>
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active 
          ? 'bg-navy text-white' 
          : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {children}
    </button>
  );
}

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    users: [],
    introductions: [],
    proofs: []
  });
  const [stats, setStats] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAllData = async () => {
    if (!token) return;
    try {
      const [usersRes, introsRes, proofsRes] = await Promise.all([
        fetch(`${API}/users/all`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/introductions/all`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/proofs/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      const users = usersRes.ok ? await usersRes.json() : [];
      const introductions = introsRes.ok ? await introsRes.json() : [];
      const proofs = proofsRes.ok ? await proofsRes.json() : [];

      setData({ users, introductions, proofs });

      // Calculate stats
      setStats({
        totalUsers: users.length,
        suppliers: users.filter(u => u.role === 'Supplier').length,
        buyers: users.filter(u => u.role === 'Buyer').length,
        reps: users.filter(u => u.role === 'Rep').length,
        activeMembers: users.filter(u => u.membership_status === 'active').length,
        pendingIntros: introductions.filter(i => i.status === 'pending').length,
        pendingProofs: proofs.filter(p => p.status === 'pending').length,
        totalPayouts: proofs.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.payout_amount || 0), 0)
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect must be before any conditional returns
  useEffect(() => {
    if (user && user.role === 'Admin') {
      fetchAllData();
    }
  }, [token, user]);

  // Redirect if not admin - AFTER hooks
  if (user && user.role !== 'Admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const updateIntroStatus = async (introId, status) => {
    setActionLoading(introId);
    try {
      await fetch(`${API}/introductions/${introId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      fetchAllData();
    } catch (err) {
      console.error('Error updating intro:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const updateProofStatus = async (proofId, status) => {
    setActionLoading(proofId);
    try {
      await fetch(`${API}/proofs/${proofId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      fetchAllData();
    } catch (err) {
      console.error('Error updating proof:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const updateMembership = async (userId, status) => {
    setActionLoading(userId);
    try {
      await fetch(`${API}/users/${userId}/membership?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAllData();
    } catch (err) {
      console.error('Error updating membership:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-copper" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-slate-50 min-h-screen">
        <div className="container-custom py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">
                Admin Dashboard
              </h1>
              <p className="text-slate-600 mt-1">Manage users, introductions, and payouts</p>
            </div>
            <button onClick={logout} className="text-sm text-slate-600 hover:text-copper">
              Sign Out
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Users" value={stats.totalUsers} color="blue" />
            <StatCard icon={MessageSquare} label="Pending Intros" value={stats.pendingIntros} color="yellow" />
            <StatCard icon={FileText} label="Pending Proofs" value={stats.pendingProofs} color="yellow" />
            <StatCard icon={DollarSign} label="Total Payouts" value={`$${stats.totalPayouts}`} color="green" />
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="border-b border-slate-200 p-4 flex gap-2">
              <Tab active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
                Users ({stats.totalUsers})
              </Tab>
              <Tab active={activeTab === 'introductions'} onClick={() => setActiveTab('introductions')}>
                Introductions ({data.introductions.length})
              </Tab>
              <Tab active={activeTab === 'proofs'} onClick={() => setActiveTab('proofs')}>
                Proofs ({data.proofs.length})
              </Tab>
            </div>

            <div className="p-6">
              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm text-slate-500 font-medium px-4">
                    <span>User</span>
                    <span>Role</span>
                    <span>Membership</span>
                    <span>Actions</span>
                  </div>
                  {data.users.map((u) => (
                    <div key={u.id} className="grid grid-cols-4 gap-4 items-center p-4 rounded-xl border border-slate-200 hover:border-copper/30">
                      <div>
                        <p className="font-medium text-navy">{u.name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium w-fit ${
                        u.role === 'Supplier' ? 'bg-blue-100 text-blue-700' :
                        u.role === 'Buyer' ? 'bg-green-100 text-green-700' :
                        u.role === 'Rep' ? 'bg-purple-100 text-purple-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {u.role}
                      </span>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium w-fit ${
                        u.membership_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {u.membership_status || 'free'}
                      </span>
                      <div className="flex gap-2">
                        {u.membership_status !== 'active' && u.role === 'Supplier' && (
                          <button
                            onClick={() => updateMembership(u.id, 'active')}
                            disabled={actionLoading === u.id}
                            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                          >
                            {actionLoading === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Activate'}
                          </button>
                        )}
                        {u.membership_status === 'active' && (
                          <button
                            onClick={() => updateMembership(u.id, 'expired')}
                            disabled={actionLoading === u.id}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Introductions Tab */}
              {activeTab === 'introductions' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-5 gap-4 text-sm text-slate-500 font-medium px-4">
                    <span>Buyer</span>
                    <span>Supplier</span>
                    <span>Message</span>
                    <span>Status</span>
                    <span>Actions</span>
                  </div>
                  {data.introductions.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No introduction requests yet</p>
                  ) : (
                    data.introductions.map((intro) => (
                      <div key={intro.id} className="grid grid-cols-5 gap-4 items-center p-4 rounded-xl border border-slate-200 hover:border-copper/30">
                        <div>
                          <p className="font-medium text-navy text-sm">{intro.buyer_name}</p>
                          <p className="text-xs text-slate-500">{intro.buyer_company}</p>
                        </div>
                        <div>
                          <p className="font-medium text-navy text-sm">{intro.supplier_name}</p>
                          <p className="text-xs text-slate-500">{intro.supplier_company}</p>
                        </div>
                        <p className="text-sm text-slate-600 truncate">{intro.message || '-'}</p>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium w-fit ${
                          intro.status === 'approved' ? 'bg-green-100 text-green-700' :
                          intro.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          intro.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {intro.status}
                        </span>
                        <div className="flex gap-2">
                          {intro.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateIntroStatus(intro.id, 'approved')}
                                disabled={actionLoading === intro.id}
                                className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                                title="Approve"
                              >
                                {actionLoading === intro.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => updateIntroStatus(intro.id, 'rejected')}
                                disabled={actionLoading === intro.id}
                                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Proofs Tab */}
              {activeTab === 'proofs' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-6 gap-4 text-sm text-slate-500 font-medium px-4">
                    <span>Rep</span>
                    <span>Account</span>
                    <span>Type</span>
                    <span>Payout</span>
                    <span>Status</span>
                    <span>Actions</span>
                  </div>
                  {data.proofs.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No proof submissions yet</p>
                  ) : (
                    data.proofs.map((proof) => (
                      <div key={proof.id} className="grid grid-cols-6 gap-4 items-center p-4 rounded-xl border border-slate-200 hover:border-copper/30">
                        <div>
                          <p className="font-medium text-navy text-sm">{proof.rep_name}</p>
                          <p className="text-xs text-slate-500">{proof.rep_email}</p>
                        </div>
                        <p className="text-sm text-navy">{proof.account_name}</p>
                        <span className="text-xs text-slate-600">
                          {proof.placement_type === 'new_btg' ? 'New BTG' : 'Feature'}
                        </span>
                        <span className="font-semibold text-navy">${proof.payout_amount}</span>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium w-fit ${
                          proof.status === 'paid' ? 'bg-green-100 text-green-700' :
                          proof.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          proof.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {proof.status}
                        </span>
                        <div className="flex gap-2">
                          {proof.photo_url && (
                            <a
                              href={proof.photo_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                              title="View Photo"
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          )}
                          {proof.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateProofStatus(proof.id, 'approved')}
                                disabled={actionLoading === proof.id}
                                className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                                title="Approve"
                              >
                                {actionLoading === proof.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => updateProofStatus(proof.id, 'rejected')}
                                disabled={actionLoading === proof.id}
                                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {proof.status === 'approved' && (
                            <button
                              onClick={() => updateProofStatus(proof.id, 'paid')}
                              disabled={actionLoading === proof.id}
                              className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                            >
                              {actionLoading === proof.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Mark Paid'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
